import React, { useState } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import { useDispatch, useSelector } from "react-redux";
import {
  FilterState,
  selectCampaignCommunicationsFilters,
  setCampaignCommunicationsOutcome,
} from "../../../redux/slices/filterSlice";
import { useGetCampaignOutcomeOptionsQuery } from "../../../redux/services/campaignApi";

interface CampaignOutcomeFilterProps {
  onClose: () => void;
  campaignId: string;
}

const CampaignOutcomeFilter = ({
  onClose,
  campaignId,
}: CampaignOutcomeFilterProps) => {
  const dispatch = useDispatch();
  const communicationMode = useSelector(
    (state: { filter: FilterState }) =>
      selectCampaignCommunicationsFilters(state)?.mode
  );
  const { data: campaignOutcomes, isLoading: isLoadingCampaignOutcomes } =
    useGetCampaignOutcomeOptionsQuery({
      use_case: campaignId,
      ...(communicationMode.value !== "all" && {
        channel: communicationMode.value as string,
      }),
    });
  const outcome = useSelector(
    (state: { filter: FilterState }) =>
      selectCampaignCommunicationsFilters(state)?.outcome
  );
  const [selectedOutcome, setSelectedOutcome] = useState<string>(
    outcome.value as string
  );

  const handleSelectOutcome = (outcome: string) => {
    setSelectedOutcome(outcome === selectedOutcome ? "" : outcome);
  };

  const handleApply = () => {
    dispatch(
      setCampaignCommunicationsOutcome({
        value: selectedOutcome,
        displayValue: selectedOutcome,
      })
    );
    onClose();
  };

  return (
    <div className="flex flex-col">
      {/* Selected Outcome Display */}
      {selectedOutcome && (
        <div className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-b border-border-gray dark:border-border-gray-dk">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
              {selectedOutcome}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
          </div>
        </div>
      )}

      {/* Outcomes List */}
      <div className="flex flex-col">
        <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
          All Outcomes
        </div>

        <div className="overflow-y-auto">
          {isLoadingCampaignOutcomes || campaignOutcomes?.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {isLoadingCampaignOutcomes
                  ? "Loading outcomes..."
                  : "No outcomes available"}
              </div>
            </div>
          ) : (
            campaignOutcomes?.map(
              (outcome: string) =>
                outcome !== selectedOutcome && (
                  <div
                    key={outcome}
                    onClick={() => handleSelectOutcome(outcome)}
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                        {outcome}
                      </div>
                    </div>
                  </div>
                )
            )
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 px-3 pb-2">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default CampaignOutcomeFilter;
